import { Col, Select } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import PreviewQuestionLabel from "../../../common/PreviewQuestionLabel/PreviewQuestionLabel";
import style from "./Ranking.module.scss";
import { ReactComponent as DragIcon } from "./DragIcon.svg";
import { useSurveyInputStore } from "../../../../surveyStore";
import PreviewQuestionConstraints from "../../../common/PreviewQuestionConstraints/PreviewQuestionConstraints";
import useEvaluateChoiceFilter from "../../question-preview/hooks/useEvaluateChoiceFilter";

export const Ranking = React.memo(
   ({ props, hasErrors, userId, surveyId, submissionId, label, hint, language }) => {
      const { id, selectQuestionChoices, questionType, isRequired } = props;
      const [value, setValue] = useState([]);

      const hasSubmissionId = Boolean(submissionId);

      // Options come from a choice list like any select, so a `choice_filter` on the
      // question has to narrow them the same way it narrows select options. A
      // submission keeps every option: its filter inputs are no longer editable.
      const visibleChoices = useEvaluateChoiceFilter({
         choiceFilter: hasSubmissionId ? null : props.choiceFilter,
         name: props.name,
         selectQuestionChoices,
         repeatInstanceIndex: props.repeatInstanceIndex,
         isRepeat: !!props.repeatInstanceIndex
      });

      const options = useMemo(
         () =>
            visibleChoices.map((choice) => ({
               id: choice.id,
               label:
                  choice.choiceLabels?.find((l) => l.languageId === language)?.text ??
                  choice.choiceLabels?.[0]?.text
            })),
         [visibleChoices, language]
      );

      const inputState = useSurveyInputStore((state) => state.inputs?.[id]);
      const setInputs = useSurveyInputStore((state) => state.setInputs);

      const wrapperStyle =
         hasErrors &&
         isRequired &&
         (!inputState ||
            !inputState?.answerDetail?.answerRankings ||
            inputState?.answerDetail?.answerRankings.length < visibleChoices.length)
            ? { border: "1px solid red", borderRadius: "7px" }
            : {};

      const items = visibleChoices.map((choice, i) => ({
         value: i + 1,
         label: i + 1
      }));

      const onChange = (e, choiceId) => {
         const existingIndex = value.findIndex((el) => el.choiceId === choiceId);

         if (existingIndex !== -1) {
            setValue((prev) => {
               const updatedValue = [...prev];
               updatedValue[existingIndex] = { choiceId, rank: e };
               return updatedValue;
            });
         } else {
            setValue((prev) => [...prev, { choiceId, rank: e }]);
         }
      };

      useEffect(() => {
         if (value.length === 0) return;
         setInputs({
            target: {
               name: id,
               value: {
                  surveyId: surveyId,
                  userId: userId,
                  questionId: id,
                  questionType: questionType,
                  name: props.name,
                  skipLogicAnswer: value,
                  answerDetail: {
                     answerRankings: value
                  }
               }
            }
         });
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [value]);

      useEffect(() => {
         if (!inputState) return;

         setValue(inputState?.answerDetail?.answerRankings);
      }, [inputState, id]);

      // An option the filter just hid must not keep its rank: the respondent can no
      // longer see or change it, so it would be submitted behind their back.
      useEffect(() => {
         if (hasSubmissionId || !props.choiceFilter || !value?.length) {
            return;
         }

         const visibleChoiceIds = new Set(visibleChoices.map((choice) => choice.id));
         const keptRankings = value.filter((ranking) => visibleChoiceIds.has(ranking.choiceId));

         if (keptRankings.length === value.length) {
            return;
         }

         setValue(keptRankings);

         if (keptRankings.length === 0) {
            // The effect above bails out on an empty list, so clear the answer here.
            setInputs({ target: { name: id, value: "" } });
         }
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [visibleChoices, value, hasSubmissionId, props.choiceFilter]);

      return (
         <Col lg={24} md={24} sm={24} xs={24}>
            <PreviewQuestionLabel label={label} isRequired={isRequired} hint={hint} />
            <PreviewQuestionConstraints constraintMessages={props.constraintMessages} />

            <div className={style.wrapper} style={wrapperStyle}>
               <div className={style.optionsList}>
                  {options.map((option) => {
                     return (
                        <div className={style.option} key={option.id}>
                           <div className={style.leftSide}>
                              <DragIcon />
                              <p>{option.label}</p>
                           </div>
                           <Select
                              style={{
                                 width: "80px",
                                 marginLeft: "10px",
                                 marginBottom: "4px"
                              }}
                              onChange={(e) => onChange(e, option.id)}
                              value={value.find((el) => el.choiceId === option.id)?.rank}
                              options={items}
                              className={`${submissionId ? "submissionStyle" : ""}`}
                           />
                        </div>
                     );
                  })}
               </div>
            </div>
            {hasErrors &&
               isRequired &&
               (!inputState ||
                  !inputState?.answerDetail?.answerRankings ||
                  inputState?.answerDetail?.answerRankings.length < visibleChoices.length) && (
                  <span style={{ color: "red" }}>All rankings must be selected</span>
               )}
         </Col>
      );
   }
);
